import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiUser, FiMail, FiLock, FiSave, FiEdit2, FiBriefcase } from 'react-icons/fi';
import { IoCubeOutline } from 'react-icons/io5';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const UserProfileModal = ({ isOpen, onClose }) => {
  const { user, updateUser, currentProject } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setEditMode(false);
      setErrors({});
    }
  }, [isOpen, user]);

  const getUserInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData.newPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Senha atual é obrigatória';
      }
      if (formData.newPassword.length < 6) {
        newErrors.newPassword = 'Mínimo 6 caracteres';
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Senhas não coincidem';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const updates = {};
      
      if (formData.name !== user.name) updates.name = formData.name;
      if (formData.email !== user.email) updates.email = formData.email;

      if (formData.newPassword) {
        const passwordResponse = await api.patch('/auth/update-password', {
          oldPassword: formData.currentPassword,
          newPassword: formData.newPassword
        });

        if (!passwordResponse.data.success && passwordResponse.data.error) {
          setErrors({ currentPassword: passwordResponse.data.error });
          setLoading(false);
          return;
        }
      }

      if (Object.keys(updates).length > 0) {
        const response = await api.patch(`/users/${user.id}`, updates);
        if (response.data.user) {
          updateUser(response.data.user);
        }
      }

      alert('Perfil atualizado com sucesso!');
      setEditMode(false);
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      alert(error.response?.data?.error || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Header com Avatar */}
        <ModalHeader>
          <HeaderLeft>
            <UserAvatar $color={user?.color || '#3b82f6'}>
              <span>{getUserInitials(user?.name)}</span>
            </UserAvatar>
            <HeaderInfo>
              <h2>{user?.name || 'Usuário'}</h2>
              <p>{user?.email || 'email@exemplo.com'}</p>
            </HeaderInfo>
          </HeaderLeft>
          <HeaderRight>
            {!editMode && (
              <EditButton onClick={() => setEditMode(true)}>
                <FiEdit2 />
                Editar
              </EditButton>
            )}
            <CloseButton onClick={onClose}>
              <FiX />
            </CloseButton>
          </HeaderRight>
        </ModalHeader>

        {/* Body com Grid */}
        <ModalBody>
          <GridContainer>
            {/* Coluna Esquerda - Informações Pessoais */}
            <LeftColumn>
              <Card>
                <CardHeader>
                  <FiUser />
                  <span>Informações Pessoais</span>
                </CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label>Nome Completo</Label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      disabled={!editMode}
                      placeholder="Seu nome completo"
                      $hasError={!!errors.name}
                    />
                    {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      disabled={!editMode}
                      placeholder="seu@email.com"
                      $hasError={!!errors.email}
                    />
                    {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label>Cargo</Label>
                    <RoleBadge>
                      <FiBriefcase />
                      {user?.cargo || 'user'}
                    </RoleBadge>
                  </FormGroup>

                  {currentProject && (
                    <ProjectFormGroup>
                      <Label>Projeto Atual</Label>
                      <ProjectCardWrapper>
                        <ProjectCard>
                          <ProjectAvatar $color={currentProject.color || '#3b82f6'}>
                            <div className="circle-bg" />
                            <IoCubeOutline />
                          </ProjectAvatar>
                          <ProjectInfo>
                            <ProjectName>{currentProject.nome}</ProjectName>
                            {currentProject.cnpj && (
                              <ProjectCNPJ>{currentProject.cnpj}</ProjectCNPJ>
                            )}
                          </ProjectInfo>
                        </ProjectCard>
                      </ProjectCardWrapper>
                    </ProjectFormGroup>
                  )}
                </CardBody>
              </Card>
            </LeftColumn>

            {/* Coluna Direita - Segurança */}
            <RightColumn>
              <Card>
                <CardHeader>
                  <FiLock />
                  <span>Segurança</span>
                </CardHeader>
                <CardBody>
                  {!editMode ? (
                    <SecurityInfo>
                      <FiLock />
                      <div>
                        <strong>Senha protegida</strong>
                        <p>Clique em "Editar" para alterar sua senha</p>
                      </div>
                    </SecurityInfo>
                  ) : (
                    <>
                      <FormGroup>
                        <Label>Senha Atual</Label>
                        <Input
                          type="password"
                          value={formData.currentPassword}
                          onChange={(e) => handleChange('currentPassword', e.target.value)}
                          placeholder="••••••••"
                          $hasError={!!errors.currentPassword}
                        />
                        {errors.currentPassword && <ErrorMessage>{errors.currentPassword}</ErrorMessage>}
                      </FormGroup>

                      <FormGroup>
                        <Label>Nova Senha</Label>
                        <Input
                          type="password"
                          value={formData.newPassword}
                          onChange={(e) => handleChange('newPassword', e.target.value)}
                          placeholder="••••••••"
                          $hasError={!!errors.newPassword}
                        />
                        {errors.newPassword && <ErrorMessage>{errors.newPassword}</ErrorMessage>}
                      </FormGroup>

                      <FormGroup>
                        <Label>Confirmar Senha</Label>
                        <Input
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => handleChange('confirmPassword', e.target.value)}
                          placeholder="••••••••"
                          $hasError={!!errors.confirmPassword}
                        />
                        {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword}</ErrorMessage>}
                      </FormGroup>

                      <PasswordHint>
                        Deixe em branco para manter a senha atual
                      </PasswordHint>
                    </>
                  )}
                </CardBody>
              </Card>
            </RightColumn>
          </GridContainer>
        </ModalBody>

        {/* Footer */}
        {editMode && (
          <ModalFooter>
            <CancelButton onClick={() => {
              setEditMode(false);
              setFormData({
                name: user?.name || '',
                email: user?.email || '',
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
              });
              setErrors({});
            }}>
              Cancelar
            </CancelButton>
            <SaveButton onClick={handleSave} disabled={loading}>
              <FiSave />
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </SaveButton>
          </ModalFooter>
        )}
      </ModalContainer>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2em;
  backdrop-filter: blur(8px);
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => theme.card};
  border-radius: 12px;
  width: 100%;
  max-width: 800px;
  height: 600px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid ${({ theme }) => theme.border};
  overflow: hidden;
  animation: modalFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const AvatarGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2em;
  font-weight: 600;
  color: white;
  box-shadow: 0 2px 8px ${({ $color }) => $color}40;
  position: relative;
  z-index: 2;
`;

const ProjectAvatar = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  .circle-bg {
    position: absolute;
    width: 100%;
    height: 100%;
    background: white;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  svg {
    position: relative;
    z-index: 1;
    font-size: 1.5em;
    color: ${({ $color }) => $color};
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
  }
`;

const ProjectCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 12px;
  background: ${({ theme }) => theme.buttonBg};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 6px;
  transition: all 0.15s ease;
  margin: 0 auto;
  width: fit-content;
  max-width: 100%;

  &:hover {
    background: ${({ theme }) => theme.card2};
  }
`;

const ProjectInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ProjectName = styled.div`
  font-size: 0.9em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProjectCNPJ = styled.div`
  font-size: 0.75em;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  font-family: monospace;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h2 {
    margin: 0;
    font-size: 1.1em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }

  p {
    margin: 0;
    font-size: 0.85em;
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
  }
`;

const ProjectTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: ${({ theme }) => theme.buttonBg};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 4px;
  font-size: 0.75em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  opacity: 0.8;
  margin-top: 2px;
  width: fit-content;

  svg {
    font-size: 1.1em;
    opacity: 0.7;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ theme }) => theme.buttonBg};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.card2};
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
  }

  svg {
    font-size: 1em;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 1.2em;

  &:hover {
    opacity: 1;
    background: ${({ theme }) => theme.buttonBg};
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.text}40;
    }
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  height: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: ${({ theme }) => theme.card2};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  font-size: 0.9em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};

  svg {
    font-size: 1.1em;
    opacity: 0.7;
  }
`;

const CardBody = styled.div`
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ProjectFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const ProjectCardWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const Label = styled.label`
  font-size: 0.8em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: ${({ theme }) => 
    theme.body === '#fafbfc' 
      ? '#ffffff' 
      : 'rgba(0, 0, 0, 0.3)'};
  border: 1px solid ${({ theme, $hasError }) => 
    $hasError ? '#ef4444' : theme.border};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};
  font-size: 0.95em;
  transition: all 0.15s ease;
  outline: none;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:focus {
    border-color: ${({ theme, $hasError }) => 
      $hasError ? '#ef4444' : theme.primary};
    background: ${({ theme }) => 
      theme.body === '#fafbfc' 
        ? '#ffffff' 
        : 'rgba(0, 0, 0, 0.4)'};
    box-shadow: 0 0 0 3px ${({ theme, $hasError }) => 
      $hasError ? 'rgba(239, 68, 68, 0.1)' : `${theme.primary}20`};
  }

  &::placeholder {
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: ${({ theme }) => theme.buttonBg};
  }
`;

const RoleBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.buttonBg};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 6px;
  font-size: 0.9em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  text-transform: uppercase;
  width: fit-content;

  svg {
    font-size: 1em;
    opacity: 0.7;
  }
`;

const SecurityInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.buttonBg};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 6px;

  svg {
    font-size: 1.3em;
    color: ${({ theme }) => theme.primary};
    margin-top: 2px;
  }

  div {
    flex: 1;

    strong {
      display: block;
      font-size: 0.95em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      margin-bottom: 4px;
    }

    p {
      margin: 0;
      font-size: 0.85em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      line-height: 1.4;
    }
  }
`;

const ErrorMessage = styled.div`
  font-size: 0.75em;
  color: #ef4444;
  margin-top: 2px;
`;

const PasswordHint = styled.div`
  font-size: 0.75em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  padding: 10px 12px;
  background: ${({ theme }) => theme.buttonBg};
  border-radius: 6px;
  margin-top: 4px;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card};
`;

const CancelButton = styled.button`
  padding: 10px 18px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.buttonBg};
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: ${({ theme }) => theme.primary};
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 1em;
  }
`;

export default UserProfileModal;
